import { MediaPreview, SegmentedControl, cn } from "@nexus/ui";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { AnimaticPlayer } from "@/features/opportunities/components/scripts/AnimaticPlayer/AnimaticPlayer";

import type { OrderScriptRow } from "../../types/marketplace.types";

type StoryboardVariant = "sketch" | "animatic";

export function OrderScriptStoryboards({
  script,
  title,
  heading,
  className,
}: {
  script: Pick<OrderScriptRow, "production">;
  title?: string | null;
  heading?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const [selectedVariant, setSelectedVariant] =
    useState<StoryboardVariant>("sketch");
  const production = script.production;
  if (!production) return null;

  const storyboardImages = Array.isArray(production.storyboardImages)
    ? production.storyboardImages
    : [];
  const storyboardPanels = Array.isArray(production.storyboardPanels)
    ? production.storyboardPanels
    : [];
  const productionScenes = Array.isArray(production.scenes)
    ? production.scenes
    : [];
  const sketches = storyboardImages.filter(
    (image): image is typeof image & { imageUrl: string } =>
      typeof image.imageUrl === "string" && image.imageUrl.length > 0,
  );
  const panels = storyboardPanels.map((panel) => ({
    panelIndex: panel.panelIndex,
    ...(panel.sceneIndex != null ? { sceneIndex: panel.sceneIndex } : {}),
    label: panel.label,
    caption: panel.caption,
    imageUrl: panel.imageUrl ?? null,
  }));
  const hasSketch = sketches.length > 0;
  const hasAnimatic = panels.some((panel) => panel.imageUrl != null);
  if (!hasSketch && !hasAnimatic) return null;

  const variant =
    selectedVariant === "sketch" && hasSketch
      ? "sketch"
      : selectedVariant === "animatic" && hasAnimatic
        ? "animatic"
        : hasSketch
          ? "sketch"
          : "animatic";
  const scenes = productionScenes.map((scene) => ({
    id: `${scene.sceneNumber}-${scene.sceneName}`,
    scene_number: scene.sceneNumber,
    scene_name: scene.sceneName,
    props: scene.props,
    seeing: scene.seeing,
    hearing: scene.hearing,
    ...(scene.product ? { product: scene.product } : {}),
  }));

  return (
    <section aria-labelledby={headingId} className={cn("flex flex-col gap-3", className)}>
      <div className="flex min-h-8 flex-wrap items-center justify-between gap-2">
        <h3
          id={headingId}
          className="text-caption tracking-label font-medium uppercase text-muted-foreground"
        >
          {heading ??
            t("marketplace.orders.list.panel.storyboard", "Storyboard")}
        </h3>
        {hasSketch && hasAnimatic ? (
          <SegmentedControl
            value={variant}
            onValueChange={(value) => {
              if (value === "sketch" || value === "animatic") {
                setSelectedVariant(value);
              }
            }}
            ariaLabel={t(
              "marketplace.orders.list.panel.storyboardVariant",
              "Storyboard style",
            )}
            tone="subtle"
            options={[
              {
                value: "sketch",
                label: t(
                  "marketplace.orders.list.panel.storyboardSketch",
                  "Sketch",
                ),
              },
              {
                value: "animatic",
                label: t(
                  "marketplace.orders.list.panel.storyboardAnimatic",
                  "Animatic",
                ),
              },
            ]}
          />
        ) : null}
      </div>

      {variant === "sketch" ? (
        <div className="flex flex-col gap-3">
          {sketches.map((image) => (
            <MediaPreview
              key={`${image.model}-${image.imageUrl}`}
              type="image"
              src={image.imageUrl}
              thumbnailSrc={image.imageUrl}
              alt={t(
                "marketplace.orders.list.panel.storyboardAlt",
                "Storyboard sketch for this script",
              )}
              className="aspect-video w-full overflow-hidden rounded-md border border-border"
            />
          ))}
        </div>
      ) : (
        <AnimaticPlayer panels={panels} scenes={scenes} title={title} />
      )}
    </section>
  );
}
